/**
 * Hook React Query para mutaciones (aprobar/rechazar) del Action Center (0666b).
 *
 * Tras mutación exitosa invalida `actionCenter.list` + `actionCenter.count`
 * + `workflow.timeline.*` para refetch automático de listado, badge y panel.
 *
 * Usa el endpoint `POST /versiones/:versionId/transitar` del motor
 * unificado (ADR-025) — el mismo que `<ArtefactoWorkflowBar>` consume vía
 * `<ConfirmModal>` desde 0661.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/services/api';
import type { ArtefactoWorkflowTipo } from './useActionCenterDetail';

export type DecisionWorkflow = 'APROBAR' | 'RECHAZAR';

export interface MutateParams {
  tipo: ArtefactoWorkflowTipo;
  id: string;
  decision: DecisionWorkflow;
  motivo?: string;
  observaciones?: string;
}

function endpointTransitar(tipo: ArtefactoWorkflowTipo, id: string): string {
  if (tipo === 'ORGANIGRAMA') return `/organigrama/versiones/${id}/transitar`;
  if (tipo === 'DOC') return `/versiones/${id}/transitar`;
  if (tipo === 'FLUJOGRAMA') return `/flujogramas/edit/${id}/transitar`;
  // PROCESO no transita aún por motor unificado.
  return `/versiones/${id}/transitar`;
}

/**
 * Cada endpoint backend acepta nombres distintos para los campos motivo/comentario:
 *   - /organigrama/versiones/:id/transitar → `motivoRechazo` (solo en RECHAZAR; no acepta observaciones).
 *   - /versiones/:id/transitar (DOC)        → `comentario` (texto único · motivo+observaciones combinados).
 *   - /flujogramas/edit/:flowchartId/transitar → idem DOC.
 *
 * Fix 0669+ (CEO reportó "da error al aceptar/rechazar"): el body legacy del
 * 0666 enviaba `motivo`+`observaciones` cross-tipo, lo que NO matchea ningún
 * endpoint y 3 de 3 dan 400 BAD_REQUEST silencioso.
 */
function bodyTransitar(
  tipo: ArtefactoWorkflowTipo,
  accion: 'APROBAR' | 'RECHAZAR',
  motivo: string | undefined,
  observaciones: string | undefined,
): Record<string, string | undefined> {
  const textoCombinado = [motivo?.trim(), observaciones?.trim()].filter(Boolean).join(' · ');

  if (tipo === 'ORGANIGRAMA') {
    return {
      accion,
      ...(accion === 'RECHAZAR' && motivo ? { motivoRechazo: motivo.trim() } : {}),
    };
  }
  // DOC + FLUJOGRAMA usan `comentario`.
  return {
    accion,
    ...(textoCombinado ? { comentario: textoCombinado } : {}),
  };
}

export function useActionCenterMutate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: MutateParams) => {
      const accion = params.decision === 'APROBAR' ? 'APROBAR' : 'RECHAZAR';
      return request(endpointTransitar(params.tipo, params.id), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          bodyTransitar(params.tipo, accion, params.motivo, params.observaciones),
        ),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['actionCenter', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['actionCenter', 'count'] });
      queryClient.invalidateQueries({
        queryKey: ['actionCenter', 'detail', variables.tipo, variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow', 'timeline', variables.tipo, variables.id],
      });
    },
  });
}
